import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { toast } from 'sonner';
import { Button } from '@pantry/ui';
import { useResendInvitation } from '@pantry/api-client';
import type { InvitationWithInviter } from '@pantry/api-client';
import {
  XMarkIcon,
  ClockIcon,
  EnvelopeIcon,
  ArrowPathIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { CancelInvitationDialog } from './CancelInvitationDialog';

interface InvitationCardProps {
  householdId: string;
  invitation: InvitationWithInviter;
  canCancel: boolean;
}

function formatExpiryDate(date: Date | string, t: ReturnType<typeof useLingui>['t']): string {
  const expiryDate = new Date(date);
  const now = new Date();
  const diffMs = expiryDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) {
    return t`Expired`;
  } else if (diffDays === 1) {
    return t`Expires tomorrow`;
  } else {
    return t`Expires in ${diffDays} days`;
  }
}

function getRoleBadgeStyles(role: string) {
  switch (role) {
    case 'admin':
      return 'bg-primary/10 text-primary border border-primary/20';
    default:
      return 'bg-muted text-muted-foreground border border-border/50';
  }
}

function getRoleLabel(role: string, t: ReturnType<typeof useLingui>['t']): string {
  switch (role) {
    case 'owner':
      return t`Owner`;
    case 'admin':
      return t`Admin`;
    case 'member':
      return t`Member`;
    default:
      return role;
  }
}

export function InvitationCard({ householdId, invitation, canCancel }: InvitationCardProps) {
  const { t } = useLingui();
  const [showCancel, setShowCancel] = useState(false);
  const resendInvitation = useResendInvitation(householdId);
  const [resendSuccess, setResendSuccess] = useState(false);

  const isExpired = new Date(invitation.expiresAt) <= new Date();

  return (
    <>
      <div
        className={`flex items-center justify-between p-3 rounded-xl border border-dashed bg-muted/20 transition-opacity ${isExpired ? 'opacity-50' : 'hover:bg-muted/30'}`}
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted/50 text-muted-foreground">
            <EnvelopeIcon className="h-5 w-5" />
          </div>
          <div>
            <p className="font-medium text-foreground">{invitation.email}</p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <ClockIcon className="h-3 w-3" />
              <span className={isExpired ? 'text-destructive font-medium' : 'text-expiring-soon'}>
                {formatExpiryDate(invitation.expiresAt, t)}
              </span>
              {invitation.invitedByUser && (
                <>
                  <span className="text-border">•</span>
                  <span>
                    <Trans>
                      Invited by {invitation.invitedByUser.name || invitation.invitedByUser.email}
                    </Trans>
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center rounded-lg px-2.5 py-1 text-xs font-medium ${getRoleBadgeStyles(invitation.role)}`}
          >
            {getRoleLabel(invitation.role, t)}
          </span>
          {canCancel && !isExpired && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10"
                onClick={async () => {
                  await resendInvitation.mutateAsync(invitation.id);
                  setResendSuccess(true);
                  toast.success(t`Invitation email sent!`);
                  setTimeout(() => setResendSuccess(false), 3000);
                }}
                disabled={resendInvitation.isPending || resendSuccess}
              >
                {resendSuccess ? (
                  <CheckCircleIcon className="h-4 w-4 text-fresh" />
                ) : resendInvitation.isPending ? (
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowPathIcon className="h-4 w-4" />
                )}
                <span className="sr-only">
                  <Trans>Resend invitation</Trans>
                </span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                onClick={() => setShowCancel(true)}
              >
                <XMarkIcon className="h-4 w-4" />
                <span className="sr-only">
                  <Trans>Cancel invitation</Trans>
                </span>
              </Button>
            </>
          )}
        </div>
      </div>

      <CancelInvitationDialog
        householdId={householdId}
        invitation={invitation}
        open={showCancel}
        onOpenChange={setShowCancel}
      />
    </>
  );
}
